class ApiService {
  private baseURL: string;

  constructor() {
    // Use environment variable or fallback to localhost
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  private handleUnauthorized() {
    console.error('Authentication failed - token may be expired');
    // Clear invalid auth data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Redirect to login if we're not already there
    if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/')) {
      window.location.href = '/login';
    }
  }

  // Generic GET method
  async get(endpoint: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (response.ok) {
        return await response.json();
      } else {
        console.error(`GET ${endpoint} failed:`, response.status);

        if (response.status === 401) {
          this.handleUnauthorized();
        }

        return { success: false, message: `Request failed with status ${response.status}` };
      }
    } catch (error) {
      console.error(`Error in GET ${endpoint}:`, error);
      return { success: false, message: 'Network error occurred' };
    }
  }

  // Generic POST method
  async post(endpoint: string, data: any = {}): Promise<any> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      if (response.ok) {
        return await response.json();
      } else {
        console.error(`POST ${endpoint} failed:`, response.status);

        if (response.status === 401) {
          this.handleUnauthorized();
        }

        const errorData = await response.json().catch(() => ({}));
        return { success: false, message: errorData.message || `Request failed with status ${response.status}` };
      }
    } catch (error) {
      console.error(`Error in POST ${endpoint}:`, error);
      return { success: false, message: 'Network error occurred' };
    }
  }

  // Generic PUT method
  async put(endpoint: string, data: any = {}): Promise<any> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      if (response.ok) {
        return await response.json();
      } else {
        console.error(`PUT ${endpoint} failed:`, response.status);

        if (response.status === 401) {
          this.handleUnauthorized();
        }

        const errorData = await response.json().catch(() => ({}));
        return { success: false, message: errorData.message || `Request failed with status ${response.status}` };
      }
    } catch (error) {
      console.error(`Error in PUT ${endpoint}:`, error);
      return { success: false, message: 'Network error occurred' };
    }
  }

  // Generic PATCH method
  async patch(endpoint: string, data: any = {}): Promise<any> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      if (response.ok) {
        return await response.json();
      } else {
        console.error(`PATCH ${endpoint} failed:`, response.status);

        if (response.status === 401) {
          this.handleUnauthorized();
        }

        const errorData = await response.json().catch(() => ({}));
        return { success: false, message: errorData.message || errorData.error || `Request failed with status ${response.status}` };
      }
    } catch (error) {
      console.error(`Error in PATCH ${endpoint}:`, error);
      return { success: false, message: 'Network error occurred' };
    }
  }

  // Generic DELETE method
  async delete(endpoint: string, data: any = {}): Promise<any> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      if (response.ok) {
        return await response.json();
      } else {
        console.error(`DELETE ${endpoint} failed:`, response.status);

        if (response.status === 401) {
          this.handleUnauthorized();
        }

        const errorData = await response.json().catch(() => ({}));
        return { success: false, message: errorData.message || `Request failed with status ${response.status}` };
      }
    } catch (error) {
      console.error(`Error in DELETE ${endpoint}:`, error);
      return { success: false, message: 'Network error occurred' };
    }
  }

  // Get user profile
  async getProfile(): Promise<any> {
    try {
      const response = await this.get('/auth/profile');
      return response;
    } catch (error) {
      console.error('Error getting profile:', error);
      return { success: false, user: null };
    }
  }

  // Update user profile
  async updateProfile(data: any): Promise<any> {
    try {
      const response = await this.put('/auth/profile', data);
      return response;
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, message: 'Failed to update profile' };
    }
  }

  // Session management methods
  async confirmSession(sessionId: string): Promise<any> {
    try {
      const response = await this.post(`/mentors/sessions/${sessionId}/confirm`);
      return response;
    } catch (error) {
      console.error('Error confirming session:', error);
      return { success: false, message: 'Failed to confirm session' };
    }
  }

  async rescheduleSession(sessionId: string, scheduledAt: string): Promise<any> {
    try {
      const response = await this.put(`/mentors/sessions/${sessionId}/reschedule`, { scheduledAt });
      return response;
    } catch (error) {
      console.error('Error rescheduling session:', error);
      return { success: false, message: 'Failed to reschedule session' };
    }
  }

  // Get session by confirmation token (for magic link confirmation page)
  async getSessionByToken(token: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseURL}/sessions/confirm/${token}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        return await response.json();
      }
      const errorData = await response.json().catch(() => ({}));
      return { success: false, ...errorData };
    } catch (error) {
      console.error('Error fetching session by token:', error);
      return { success: false, error: 'Failed to fetch session details' };
    }
  }

  // Confirm session via magic link token (no auth required)
  async confirmSessionByToken(token: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseURL}/sessions/confirm/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        return await response.json();
      }
      const errorData = await response.json().catch(() => ({}));
      return { success: false, ...errorData };
    } catch (error) {
      console.error('Error confirming session by token:', error);
      return { success: false, error: 'Failed to confirm session' };
    }
  }

  // ============================================
  // MESSAGING API METHODS
  // ============================================

  // Get list of conversations (users the current user can message)
  async getConversations(): Promise<{
    success: boolean;
    conversations: Array<{
      user: { id: string; name: string; profilePicture: string | null; email: string };
      connectionType: string;
      lastMessage: { content: string; createdAt: string; isFromMe: boolean } | null;
      unreadCount: number;
    }>;
  }> {
    try {
      const response = await this.get('/messages/conversations');
      return response;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return { success: false, conversations: [] };
    }
  }

  // Get message history with a specific user
  async getMessages(userId: string, limit: number = 50, offset: number = 0): Promise<{
    success: boolean;
    user?: { id: string; name: string; profilePicture: string | null; email: string };
    connectionType?: string;
    messages: Array<{
      id: string;
      content: string;
      createdAt: string;
      isFromMe: boolean;
      sender: { id: string; name: string; profilePicture: string | null };
    }>;
  }> {
    try {
      const response = await this.get(`/messages/${userId}?limit=${limit}&offset=${offset}`);
      return response;
    } catch (error) {
      console.error('Error fetching messages:', error);
      return { success: false, messages: [] };
    }
  }

  // Send a message to a user
  async sendMessage(userId: string, content: string): Promise<{
    success: boolean;
    message?: {
      id: string;
      content: string;
      createdAt: string;
      isFromMe: boolean;
      sender: { id: string; name: string; profilePicture: string | null };
    };
    error?: string;
  }> {
    try {
      const response = await this.post(`/messages/${userId}`, { content });
      return response;
    } catch (error) {
      console.error('Error sending message:', error);
      return { success: false, error: 'Failed to send message' };
    }
  }

  // Get remaining mentor sessions for a student+mentor pair
  async getRemainingMentorSessions(mentorProfileId: string): Promise<{
    success: boolean;
    remainingSessions: number;
    totalSessions: number;
    completedSessions: number;
  }> {
    try {
      const response = await this.get(`/mentors/remaining-sessions/${mentorProfileId}`);
      return response;
    } catch (error) {
      console.error('Error fetching remaining mentor sessions:', error);
      return { success: false, remainingSessions: 0, totalSessions: 0, completedSessions: 0 };
    }
  }

  // Schedule a session using an existing credit (no payment needed)
  async scheduleMentorSessionCredit(mentorProfileId: string, scheduledAt: string, recordingEnabled: boolean): Promise<{
    success: boolean;
    session?: any;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await this.post('/mentors/sessions/schedule-credit', {
        mentorProfileId,
        scheduledAt,
        recordingEnabled
      });
      return response;
    } catch (error) {
      console.error('Error scheduling session with credit:', error);
      return { success: false, error: 'Failed to schedule session' };
    }
  }

  // Book a free 15-minute intro call with a mentor
  async bookIntroCall(mentorProfileId: string, scheduledAt: string): Promise<{
    success: boolean;
    data?: any;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseURL}/mentors/sessions/book-intro`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ mentorProfileId, scheduledAt })
      });

      const result = await response.json().catch(() => ({}));
      if (response.ok) {
        return result;
      }
      return { success: false, error: result.error || 'Failed to book intro call' };
    } catch (error) {
      console.error('Error booking intro call:', error);
      return { success: false, error: 'Failed to book intro call' };
    }
  }

  // ============================================
  // SESSION NOTES API METHODS
  // ============================================

  // Update session notes (for mentors)
  async updateMentorSessionNotes(sessionId: string, notes: string): Promise<{
    success: boolean;
    session?: { id: string; meetingNotes: string };
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseURL}/mentors/sessions/${sessionId}/notes`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ notes })
      });

      if (response.ok) {
        return await response.json();
      }
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.error || 'Failed to update notes' };
    } catch (error) {
      console.error('Error updating mentor session notes:', error);
      return { success: false, error: 'Failed to update notes' };
    }
  }

  // ============================================
  // RESOURCE CENTER API METHODS
  // ============================================

  // Get my resources (for mentors)
  async getMyResources(): Promise<{
    success: boolean;
    resources: Array<{
      id: string;
      originalName: string;
      fileUrl: string;
      fileType: string;
      fileSize: number;
      description: string | null;
      createdAt: string;
      sharedWith: Array<{
        id: string;
        studentId: string;
        studentName: string;
        studentPicture: string | null;
        message: string | null;
        sessionId: string | null;
        sharedAt: string;
      }>;
    }>;
  }> {
    try {
      const response = await this.get('/resources');
      return response;
    } catch (error) {
      console.error('Error fetching resources:', error);
      return { success: false, resources: [] };
    }
  }

  // Upload a resource
  async uploadResource(file: File, description?: string): Promise<{
    success: boolean;
    resource?: {
      id: string;
      originalName: string;
      fileUrl: string;
      fileType: string;
      fileSize: number;
      description: string | null;
      createdAt: string;
    };
    error?: string;
  }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (description) {
        formData.append('description', description);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${this.baseURL}/resources/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        return await response.json();
      }
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.error || 'Failed to upload resource' };
    } catch (error) {
      console.error('Error uploading resource:', error);
      return { success: false, error: 'Failed to upload resource' };
    }
  }

  // Delete a resource
  async deleteResource(resourceId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.delete(`/resources/${resourceId}`);
      return response;
    } catch (error) {
      console.error('Error deleting resource:', error);
      return { success: false, error: 'Failed to delete resource' };
    }
  }

  // Share a resource with a student
  async shareResource(resourceId: string, studentId: string, sessionId?: string, message?: string): Promise<{
    success: boolean;
    share?: {
      id: string;
      studentId: string;
      studentName: string;
      studentPicture: string | null;
      message: string | null;
      sessionId: string | null;
      sharedAt: string;
    };
    error?: string;
  }> {
    try {
      const response = await this.post(`/resources/${resourceId}/share`, {
        studentId,
        sessionId,
        message
      });
      return response;
    } catch (error) {
      console.error('Error sharing resource:', error);
      return { success: false, error: 'Failed to share resource' };
    }
  }

  // Get all resources shared with me (for students)
  async getSharedResources(): Promise<{
    success: boolean;
    resources: Array<{
      shareId: string;
      message: string | null;
      sessionId: string | null;
      sharedAt: string;
      resource: {
        id: string;
        originalName: string;
        fileUrl: string;
        fileType: string;
        fileSize: number;
        description: string | null;
        createdAt: string;
      };
      sharedBy: {
        id: string;
        name: string;
        profilePicture: string | null;
      };
    }>;
  }> {
    try {
      const response = await this.get('/resources/shared');
      return response;
    } catch (error) {
      console.error('Error fetching shared resources:', error);
      return { success: false, resources: [] };
    }
  }

  // ============================================
  // MARKETPLACE API METHODS
  // ============================================

  // Public mentor availability (no auth required)
  async getMentorPublicAvailability(mentorProfileId: string, weekStart: string, weekEnd: string): Promise<{
    success: boolean;
    availability: Array<{ date: string; time: string; isAvailable: boolean }>;
    timezone?: string;
  }> {
    try {
      const response = await fetch(
        `${this.baseURL}/mentors/${mentorProfileId}/availability?weekStart=${encodeURIComponent(weekStart)}&weekEnd=${encodeURIComponent(weekEnd)}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (response.ok) {
        return await response.json();
      }
      return { success: false, availability: [] };
    } catch (error) {
      console.error('Error fetching mentor availability:', error);
      return { success: false, availability: [] };
    }
  }

  // Submit a session review (one per session)
  async submitMentorReview(sessionId: string, data: {
    rating: number;
    comment?: string;
    title?: string;
    helpfulnessRating?: number;
    communicationRating?: number;
    knowledgeRating?: number;
  }): Promise<{
    success: boolean;
    review?: any;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseURL}/mentors/sessions/${sessionId}/review`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      if (response.ok) {
        return await response.json();
      }
      if (response.status === 401) {
        this.handleUnauthorized();
      }
      const errorData = await response.json().catch(() => ({}));
      // Surfaces 'already_reviewed' so callers can treat it as a success state
      return { success: false, error: errorData.error || 'Failed to submit review' };
    } catch (error) {
      console.error('Error submitting review:', error);
      return { success: false, error: 'Failed to submit review' };
    }
  }

  // Join a mentor waitlist segment
  async joinWaitlist(data: { segment: string; mentorType: string; note?: string }): Promise<{
    success: boolean;
    alreadyOnList?: boolean;
    error?: string;
  }> {
    return this.post('/waitlist', data);
  }

  // Family profile (kids + preferences)
  async getFamilyProfile(): Promise<{
    success: boolean;
    children: Array<{ id: string; firstName: string; grade: string | null; activity: string | null; teamNumber: string | null; schoolType: string | null }>;
    preferences: { region: string | null; targetTier: string | null; note: string | null } | null;
  }> {
    const response = await this.get('/family/profile');
    return { children: [], preferences: null, ...response };
  }

  async updateFamilyProfile(data: {
    children?: Array<{ firstName: string; grade?: string; activity?: string; teamNumber?: string; schoolType?: string }>;
    preferences?: { region?: string; targetTier?: string; note?: string };
  }): Promise<{ success: boolean; error?: string }> {
    return this.put('/family/profile', data);
  }

  // Admin: verification queue (applicants with achievements + proof documents)
  async getVerificationQueue(): Promise<{
    success: boolean;
    pendingCount: number;
    applicants: Array<{
      profileId: string;
      displayName: string;
      email: string | null;
      university: string | null;
      classYear: string | null;
      mentorType: string;
      occupation: string | null;
      isVerified: boolean;
      pricePerSession: number;
      appliedAt: string;
      achievements: Array<{
        id: string;
        title: string;
        category: string;
        year: number | null;
        rank: string | null;
        activityTier: string | null;
        verificationStatus: string;
        verificationRecordId: string | null;
        verifiedAt: string | null;
        documents: Array<{ id: string; url: string; fileName: string; uploadedAt: string; status: string }>;
      }>;
    }>;
  }> {
    const response = await this.get('/admin/verification/queue');
    return { pendingCount: 0, applicants: [], ...response };
  }

  async reviewClaim(achievementId: string, action: 'verify' | 'self_report' | 'reject', recordId?: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    return this.post(`/admin/verification/claims/${achievementId}`, { action, recordId });
  }

  // Admin: waitlist overview
  async getAdminWaitlist(): Promise<{
    success: boolean;
    total: number;
    segments: Array<{ segment: string; count: number }>;
    entries: any[];
  }> {
    const response = await this.get('/admin/waitlist');
    return { total: 0, segments: [], entries: [], ...response };
  }

  // Admin: user management
  async getAdminUsers(search: string = '', limit: number = 25, offset: number = 0): Promise<{
    success: boolean;
    users: Array<{
      id: string;
      name: string;
      email: string;
      userType: string;
      createdAt: string;
      isActive: boolean;
      mentorProfile: { id: string } | null;
    }>;
    total: number;
  }> {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (search) params.set('search', search);
    const response = await this.get(`/admin/users?${params.toString()}`);
    return { users: [], total: 0, ...response };
  }

  async setUserActive(userId: string, isActive: boolean): Promise<{ success: boolean; error?: string; message?: string }> {
    return this.patch(`/admin/users/${userId}`, { isActive });
  }

  // Offering templates (public: active only, for the mentor offerings editor)
  async getOfferingTemplates(): Promise<{
    success: boolean;
    templates: Array<{
      id: string;
      category: string;
      activity: string;
      name: string;
      description: string | null;
      features: string[];
      sessionsCount: number;
    }>;
  }> {
    const response = await this.get('/mentors/offering-templates');
    return { templates: [], ...response };
  }

  // Admin: offering template management
  async getAdminOfferingTemplates(): Promise<{ success: boolean; templates: any[]; error?: string }> {
    const response = await this.get('/admin/offering-templates');
    return { templates: [], ...response };
  }

  async createOfferingTemplate(data: any): Promise<{ success: boolean; template?: any; error?: string }> {
    return this.post('/admin/offering-templates', data);
  }

  async updateOfferingTemplate(id: string, data: any): Promise<{ success: boolean; template?: any; error?: string }> {
    return this.put(`/admin/offering-templates/${id}`, data);
  }

  async deleteOfferingTemplate(id: string): Promise<{ success: boolean; error?: string }> {
    return this.delete(`/admin/offering-templates/${id}`);
  }

  // Activities (public: active only, drives signup dropdowns + marketplace chips)
  async getActivities(): Promise<{
    success: boolean;
    activities: Array<{
      id: string;
      name: string;
      category: string;
      sortOrder: number;
    }>;
  }> {
    const response = await this.get('/mentors/activities');
    return { activities: [], ...response };
  }

  // Admin: activity management
  async getAdminActivities(): Promise<{ success: boolean; activities: any[]; error?: string }> {
    const response = await this.get('/admin/activities');
    return { activities: [], ...response };
  }

  async createActivity(data: any): Promise<{ success: boolean; activity?: any; error?: string }> {
    return this.post('/admin/activities', data);
  }

  async updateActivity(id: string, data: any): Promise<{ success: boolean; activity?: any; error?: string }> {
    return this.put(`/admin/activities/${id}`, data);
  }

  async deleteActivity(id: string): Promise<{ success: boolean; error?: string }> {
    return this.delete(`/admin/activities/${id}`);
  }
}

export const apiService = new ApiService();
